// src/pages/PowerTranzTest.js

import React, { useState, useEffect } from 'react';
import {
  Container,
  Form,
  Button,
  Spinner,
  Alert,
  Row,
  Col
} from 'react-bootstrap';
import {
  FaCreditCard,
  FaUser,
  FaEnvelope,
  FaMoneyBillWave
} from 'react-icons/fa';
import { useLocation } from 'react-router-dom';

const PowerTranzTest = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    cardPan: '',
    cardCvv: '',
    cardExpiration: '',
    cardholderName: '',
    amount: 200,
    phone: '',
    email: ''
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const amt = params.get('amount');
    if (amt) setFormData(f => ({ ...f, amount: parseFloat(amt) }));
  }, [location.search]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleExpirationChange = e => {
    let raw = e.target.value.replace(/\D/g, '');
    if (raw.length > 4) raw = raw.slice(0, 4);
    const formatted = raw.length > 2
      ? `${raw.slice(0, 2)}/${raw.slice(2)}`
      : raw;
    setFormData(prev => ({ ...prev, cardExpiration: formatted }));
  };

  const generateUUID = () =>
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c =>
      (Math.random() * 16 | 0).toString(16)
    );

  const handleSubmit = async e => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const {
      cardPan,
      cardCvv,
      cardExpiration,
      cardholderName,
      amount,
      phone,
      email
    } = formData;

    if (!cardPan || !cardCvv || !cardExpiration || !cardholderName || !phone || !email) {
      setError('Todos los campos son obligatorios.');
      setLoading(false);
      return;
    }

    const match = cardExpiration.match(/^(\d{2})\/(\d{2})$/);
    if (!match) {
      setError('Formato de expiración inválido. Use MM/AA');
      setLoading(false);
      return;
    }
    const [, mm, yy] = match;
    const finalExpiration = `${yy}${mm}`; // YYMM

    const payload = {
      TransactionIdentifier: generateUUID(),
      TotalAmount: Math.round(Number(amount)),
      CurrencyCode: '188',
      ThreeDSecure: true,
      source: {
        CardPan: cardPan,
        CardCvv: cardCvv,
        CardExpiration: finalExpiration,
        CardholderName: cardholderName
      },
      OrderIdentifier: 'ORD-' + generateUUID(),
      AddressMatch: false,
      BillingAddress: {
        EmailAddress: email,
        PhoneNumber: phone
      },
      ExtendedData: {
        ThreeDSecure: {
          ChallengeWindowSize: 4,
          ChallengeIndicator: '03'
        },
        MerchantResponseUrl: 'https://us-central1-arzu-9380c.cloudfunctions.net/api/merchantResponse'
      }
    };

    console.log('📤 Enviando a PowerTranz:', payload);

    try {
      const response = await fetch(
        'https://us-central1-arzu-9380c.cloudfunctions.net/api/createPowerTranzSale',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

      const text = await response.text();
      console.log('🧾 Raw PowerTranz response:', text);
      const data = JSON.parse(text);
      console.log('🔍 RedirectData:', data.RedirectData);

      if (data.IsoResponseCode === 'SP4' && data.RedirectData) {
        document.open();
        document.write(data.RedirectData);
        document.close();
      } else {
        const msgs = data.Errors?.map(err => err.Message).join(', ');
        setError(msgs || 'Error inesperado en la transacción.');
      }
    } catch (err) {
      console.error('❌ Error de red:', err);
      setError('❌ Error de conexión con el servidor.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: '#f4f7fc', minHeight: '100vh', paddingTop: 50 }}>
      <Container className="shadow p-4 bg-white rounded" style={{ maxWidth: 700 }}>
        <h3 className="text-center mb-4">💳 Pago seguro con PowerTranz</h3>
        {error && <Alert variant="danger" className="text-center mb-4">{error}</Alert>}
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label><FaCreditCard className="me-2"/>Número de tarjeta</Form.Label>
            <Form.Control
              type="text"
              name="cardPan"
              value={formData.cardPan}
              onChange={handleChange}
              placeholder="1234 5678 9012 3456"
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label><FaUser className="me-2"/>Nombre en la tarjeta</Form.Label>
            <Form.Control
              type="text"
              name="cardholderName"
              value={formData.cardholderName}
              onChange={handleChange}
              placeholder="Tester User"
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label><FaUser className="me-2"/>Teléfono</Form.Label>
            <Form.Control
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="8888-8888"
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label><FaEnvelope className="me-2"/>Correo electrónico</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="tucorreo@ejemplo.com"
              required
            />
          </Form.Group>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Label>Expiración (MM/AA)</Form.Label>
              <Form.Control
                name="cardExpiration"
                value={formData.cardExpiration}
                onChange={handleExpirationChange}
                placeholder="12/25"
                required
              />
            </Col>
            <Col md={6} className="mb-3">
              <Form.Label>CVV</Form.Label>
              <Form.Control
                name="cardCvv"
                value={formData.cardCvv}
                onChange={handleChange}
                placeholder="123"
                required
              />
            </Col>
          </Row>
          <Form.Group className="mb-4">
            <Form.Label><FaMoneyBillWave className="me-2"/>Monto</Form.Label>
            <Form.Control
              name="amount"
              type="number"
              value={formData.amount}
              readOnly
            />
          </Form.Group>
          <Button
            type="submit"
            disabled={loading}
            className="w-100"
            style={{ backgroundColor: '#00C2FF', borderColor: '#00C2FF' }}
          >
            {loading ? <Spinner animation="border" size="sm"/> : 'Pagar'}
          </Button>
        </Form>
      </Container>
    </div>
  );
};

export default PowerTranzTest;
